/**
 * T1113 · Platform adapter factory · web composition.
 *
 * Composes the canonical 9 slots into a single
 * `PlatformAdapter` value. Web bundlers resolve this file via the
 * package's conditional `exports` map (the RN counterpart is
 * `../react-native/create-platform-adapter.ts`).
 *
 * **Not slots** (spec L2956 — see T1101 description): `broadcast`,
 * `csrf`, `idempotencyKey`. They live in SDK internals via implicit
 * platform forks. This factory does NOT touch them.
 */

import type {
  BooleanRef,
  PlatformAdapter,
  SyncStorageLike,
  VisibilityRefSlot,
} from '../adapter.js';
import { createWebAppStateSlot, type WebAppStateDeps } from './app-state.js';
import { openBroadcast } from './broadcast.js';
import { createWebNetInfoSlot, type WebNetInfoDeps } from './net-info.js';
import {
  type CreateAsyncStoragePersister,
  createWebQueryPersisterSlot,
} from './query-persister.js';
import { createWebRowAnimatorSlot } from './row-animator.js';
import { createWebScreenFocusSlot } from './screen-focus.js';
import { createWebSecureStoreSlot } from './secure-store.js';
import { createWebStorageSlot, type IdbKv } from './storage.js';

/**
 * Build a `VisibilityRefSlot` from the web `IntersectionObserver`
 * factory primitive. The slot exposes a `currentRef` boolean handle +
 * a `register` push/pop. Multiple acquires push onto a stack and the
 * cleanup pops; readers see the most-recent ref.
 */
function createWebVisibilityRefSlot(): VisibilityRefSlot {
  const fallback: BooleanRef = { current: false };
  const stack: BooleanRef[] = [fallback];
  return {
    get currentRef(): BooleanRef {
      return stack[stack.length - 1] ?? fallback;
    },
    register(ref: BooleanRef): () => void {
      stack.push(ref);
      return (): void => {
        const idx = stack.lastIndexOf(ref);
        if (idx !== -1 && idx !== 0) stack.splice(idx, 1);
      };
    },
    observeElement(
      el: Element,
      onIntersect: (intersecting: boolean) => void,
      options?: { readonly rootMargin?: string },
    ): () => void {
      if (typeof IntersectionObserver === 'undefined') return (): void => {};
      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            onIntersect(entry.isIntersecting);
          }
        },
        { rootMargin: options?.rootMargin ?? '0px' },
      );
      io.observe(el);
      let disposed = false;
      return (): void => {
        if (disposed) return;
        disposed = true;
        io.disconnect();
      };
    },
  };
}

/** Bundle of injected concrete primitives. Pure structural shape. */
export interface CreateWebPlatformAdapterDeps {
  readonly idb: IdbKv;
  readonly createAsyncStoragePersister: CreateAsyncStoragePersister;
  readonly appState: WebAppStateDeps;
  readonly netInfo?: WebNetInfoDeps;
  readonly localStorage?: SyncStorageLike;
  /** Network-scoped persister storage key (see `createWebDefaultAdapter`'s
   *  `cacheNamespace`). Defaults to the shared `WEB_PERSISTER_KEY`. */
  readonly persisterKey?: string;
}

/**
 * Compose the 9-slot adapter for web. `kind: 'web'` discriminator pins
 * the platform identity at the type level.
 */
export function createWebPlatformAdapter(deps: CreateWebPlatformAdapterDeps): PlatformAdapter {
  const storageDeps =
    deps.localStorage !== undefined
      ? { idb: deps.idb, localStorage: deps.localStorage }
      : { idb: deps.idb };
  const keyDep = deps.persisterKey !== undefined ? { key: deps.persisterKey } : {};
  const persisterDeps =
    deps.localStorage !== undefined
      ? {
          idb: deps.idb,
          createAsyncStoragePersister: deps.createAsyncStoragePersister,
          localStorage: deps.localStorage,
          ...keyDep,
        }
      : {
          idb: deps.idb,
          createAsyncStoragePersister: deps.createAsyncStoragePersister,
          ...keyDep,
        };
  return {
    kind: 'web',
    storage: createWebStorageSlot(storageDeps),
    secureStorage: createWebSecureStoreSlot(),
    queryPersister: createWebQueryPersisterSlot(persisterDeps),
    appState: createWebAppStateSlot(deps.appState),
    netInfo: createWebNetInfoSlot(deps.netInfo ?? {}),
    visibilityRef: createWebVisibilityRefSlot(),
    screenFocus: createWebScreenFocusSlot(),
    broadcast: { openBroadcast },
    rowAnimator: createWebRowAnimatorSlot(),
  };
}
